import React, { useEffect } from 'react';
import { Button } from '@goodrx/matisse';
import { TypingIndicator } from 'app/components/TypingIndicator/TypingIndicator';
import { ChoiceId, DiagnoseResponse } from 'app/types/Infermedica';
import { diagnose } from 'app/lib/infermedica';
import { usePrevious } from 'app/hooks';
import { TriageBlockConfig, TriageBlockChildProps } from '../TriageBlock';
import { SenderChatBubble, StyledForm, createDiagnosisPayload, SavedResponseWrapper } from '../shared';

type DiagnoseData = {
  prompt?: DiagnoseResponse;
  symptomId?: string;
  responseText?: string;
  responseId?: ChoiceId;
};

export const code: TriageBlockConfig['code'] = 'diagnose';

export const codePostfix: TriageBlockConfig['codePostfix'] = true;

export const Message: TriageBlockConfig<DiagnoseData>['Message'] = ({
  dataValues,
  dataValue,
  setCurrentDataValue,
  sessionId,
  blockCode,
}) => {
  const prompt = dataValue?.prompt;

  const beginDiagnosis = async () => {
    if (dataValues?.evidence?.[blockCode]) {
      return;
    }

    const payload = createDiagnosisPayload(dataValues);
    const data = await diagnose({ ...payload, sessionId });

    setCurrentDataValue({
      ...dataValue,
      prompt: data,
    });
  };

  useEffect(() => {
    beginDiagnosis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!prompt) {
    return <TypingIndicator />;
  }

  const { question } = prompt;

  return <SenderChatBubble>{question.text}</SenderChatBubble>;
};

export const Response: TriageBlockConfig<DiagnoseData>['Response'] = ({
  inputValue,
  setCurrentInputValue,
  setCurrentDataValue,
  dataValue,
  onSubmit,
}) => {
  const prompt = dataValue?.prompt;
  const lastInputValue = usePrevious(inputValue);

  useEffect(() => {
    if (inputValue && lastInputValue !== inputValue) {
      onSubmit();
    }
  }, [inputValue, lastInputValue, onSubmit]);

  if (!prompt) {
    return null;
  }

  const {
    question: { items },
  } = prompt;

  const [{ id, choices }] = items;

  return (
    <StyledForm>
      {choices.map((choice) => (
        <Button
          mb={2}
          key={choice.id}
          variant="grey"
          data-qa="question-response-btn"
          onClick={() => {
            const selectedChoice = choices.find((c) => c.id === choice.id);
            setCurrentInputValue(choice.id);
            setCurrentDataValue({
              symptomId: id,
              responseText: selectedChoice?.label,
              responseId: selectedChoice?.id,
            });
          }}>
          {choice.label}
        </Button>
      ))}
    </StyledForm>
  );
};

export const onSubmit: TriageBlockConfig<DiagnoseData>['onSubmit'] = async ({
  blockCode,
  dataValue,
  setDataValue,
  addBlock,
}) => {
  const { symptomId, responseId, prompt } = dataValue;

  setDataValue('evidence', {
    [blockCode]: [
      {
        id: symptomId!,
        choiceId: responseId!,
      },
    ],
  });

  if (prompt?.shouldStop) {
    addBlock('evaluate', dataValue);
    return;
  }

  addBlock('diagnose');
};

export const SavedResponse: React.FC<TriageBlockChildProps<DiagnoseData>> = ({ dataValue, ...props }) => (
  <SavedResponseWrapper {...props}>{dataValue?.responseText}</SavedResponseWrapper>
);
